"use client";

/**
 * The `metrics` report section — every `ReportMetricRow` grouped by cadence
 * into `.rv-card.rv-rows` lists of `.rv-row.entry` rows.
 *
 * Extracted from the design source `compass-oppreport.jsx` (the `metrics`
 * section) in Claude Design project `79357508-fe09-4122-831f-a0014c6a31b0`:
 * `.rv-card`, `.rv-rows`, `.rv-row`, `.rv-row.entry`, `.rv-rowtop`,
 * `.rv-rowname`, `.rv-rowval.as`, `.rv-rowper`, `.rv-rowdesc`, `.rv-entrygo`,
 * and the shift cluster `.rv-shift`, `.rv-shlabel`, `.rv-shnums`,
 * `.rv-shfrom`, `.rv-shto`, `.rv-shunit`, `.rv-shpct`.
 *
 * Rows group by `ReportMetricRow.cadence` under "Annual" / "One-time"
 * `ReportSectionHeading` subheads. Per the plan's product decision, a stream
 * is One-time only when explicitly so; everything else counts as Annual, so
 * a metric never silently drops out of the report. Today every fixture's
 * metrics are all-annual, so the One-time group always renders the design's
 * "Nothing one-time here yet" empty state rather than disappearing.
 *
 * The `.rv-shift` row is §5 gap #5 — there is no backing field for it today.
 * It renders only when `shift !== null` **and** `ready`, so the row reads
 * correctly in the common case where no shift exists yet.
 */

import { ArrowR } from "../lib/icons";
import { ReportSectionHeading } from "./ReportSectionHeading";
import type {
  ReportMetricRow,
  ReportMetricShift,
  ReportTraceTarget,
} from "./report-types";

export interface ReportMetricsListProps {
  metrics: ReportMetricRow[];
  /** `figures.ready` — the single computed gate, matching the design's `calc`. */
  ready: boolean;
  onOpenTrace?: (target: ReportTraceTarget) => void;
}

/* ------------------------------------------------------------------ *
 * Shift cluster
 * ------------------------------------------------------------------ */

/**
 * `.rv-shpct` — the percent-change pill. `dir: "down"` renders the success
 * tokens with a `−` prefix; `"up"` renders the cobalt tokens with a `+`
 * prefix. The caller omits the pill entirely when `pct` is `null`.
 */
function ShiftPercentPill({
  pct,
  dir,
}: {
  pct: string;
  dir: ReportMetricShift["dir"];
}) {
  const isDown = dir === "down";
  return (
    <span
      className={[
        "inline-flex items-center whitespace-nowrap",
        "rounded-full px-[7px] py-[2px] text-[11.5px] font-bold",
        isDown
          ? "bg-(--status-success-bg) text-(--status-success-text)"
          : "bg-(--accent-cobalt-bg) text-(--accent-cobalt)",
      ].join(" ")}
    >
      {isDown ? "−" : "+"}
      {pct}
    </span>
  );
}

/**
 * `.rv-shift` — the baseline → projected line: `.rv-shlabel` names the shift,
 * `.rv-shnums` (`.rv-shfrom` → `.rv-shto` `.rv-shunit`) carries the numbers,
 * and `.rv-shpct` closes it out when a percent is available.
 */
function MetricShiftRow({ shift }: { shift: ReportMetricShift }) {
  return (
    <div
      className={[
        "mt-2.5 flex w-full flex-wrap items-center gap-3 text-left",
        "rounded-(--radius-field) border border-(--border-subtle)",
        "bg-(--bg-canvas) px-3 py-[9px]",
      ].join(" ")}
    >
      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-(--fg-muted)">
        {shift.label}
      </span>
      <span className="ml-auto inline-flex items-baseline gap-2">
        <span className="text-[14px] font-medium text-(--fg-muted) line-through">
          {shift.from}
        </span>
        <span className="self-center text-(--fg-subtle)">
          <ArrowR size={13} />
        </span>
        <span className="text-[15px] font-bold text-(--fg-primary)">
          {shift.to}
        </span>
        <span className="text-[11px] text-(--fg-muted)">{shift.unit}</span>
      </span>
      {shift.pct !== null && (
        <ShiftPercentPill pct={shift.pct} dir={shift.dir} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Row
 * ------------------------------------------------------------------ */

/**
 * Builds the drill-in target for a metric row.
 *
 * CRITICAL: keyed by `row.traceNodeId`, never `row.id` — `id` is the
 * read-model slug and a React key only, while `traceNodeId` is the key into
 * `ReportData.traceNodes`. `formula`/`aggregate` are left `null`: a metric
 * has no formula of its own at this layer, and `FormulaTraceModal` resolves
 * the real aggregate by looking up `nodes[target.id]` itself.
 */
function buildMetricTraceTarget(row: ReportMetricRow): ReportTraceTarget {
  return {
    id: row.traceNodeId,
    kind: "metric",
    label: row.label,
    value: row.amount,
    formula: null,
    aggregate: null,
  };
}

interface MetricRowProps {
  row: ReportMetricRow;
  ready: boolean;
  onOpenTrace?: (target: ReportTraceTarget) => void;
}

/**
 * `.rv-row.entry` — one metric: `.rv-rowtop` (`.rv-rowname` + `.rv-rowval.as`
 * with its `.rv-rowper` suffix), the optional `.rv-shift` line, and
 * `.rv-rowdesc`. Clicking the row opens the formula trace, which is the only
 * thing a report row does — as in the design.
 */
function MetricRow({ row, ready, onOpenTrace }: MetricRowProps) {
  const interactive = onOpenTrace !== undefined;

  function openTrace() {
    onOpenTrace?.(buildMetricTraceTarget(row));
  }

  const content = (
    <>
      {/* .rv-rowtop */}
      <div className="flex items-baseline gap-3">
        <span className="flex-[0_0_260px] max-[860px]:flex-[0_0_160px] text-[13.5px] font-medium text-(--fg-muted)">
          {row.label}
        </span>
        {/* .rv-rowval.as, with the .rv-rowper suffix */}
        <span className="ml-auto shrink-0 whitespace-nowrap font-[family-name:var(--font-mono)] text-[16px] font-bold text-(--fg-primary)">
          {row.amount}
          <span className="ml-[5px] font-[family-name:var(--font-body)] text-[11px] font-medium text-(--fg-muted)">
            {row.cadence === "annual"
              ? "per year"
              : row.cadence === "one-time-with-rollout"
                ? "as rollout progresses"
                : "one time"}
          </span>
        </span>
      </div>

      {row.shift !== null && ready && <MetricShiftRow shift={row.shift} />}

      {row.description && (
        <p className="m-0 mt-1.5 text-[12.5px] leading-[1.5] text-(--fg-secondary)">
          {row.description}
        </p>
      )}
    </>
  );

  const rowClassName = [
    "group relative flex flex-col",
    "border-t border-(--border-subtle) px-[18px] py-4 first:border-t-0",
    "font-[family-name:var(--font-body)]",
  ].join(" ");

  if (!interactive) {
    return <div className={rowClassName}>{content}</div>;
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: hosts a nested button and a paragraph, neither valid inside a native <button>.
    <div
      role="button"
      tabIndex={0}
      className={[
        rowClassName,
        "cursor-pointer transition-colors duration-300 ease-in-out hover:bg-(--bg-sky)",
      ].join(" ")}
      onClick={openTrace}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        openTrace();
      }}
    >
      {content}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Cadence group
 * ------------------------------------------------------------------ */

interface MetricRowsCardProps {
  rows: ReportMetricRow[];
  ready: boolean;
  onOpenTrace?: (target: ReportTraceTarget) => void;
}

/** `.rv-card.rv-rows` — the bordered list wrapping one cadence group. */
function MetricRowsCard({ rows, ready, onOpenTrace }: MetricRowsCardProps) {
  return (
    <div className="mb-3 overflow-hidden rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white)">
      {rows.map((row) => (
        <MetricRow
          key={row.id}
          row={row}
          ready={ready}
          onOpenTrace={onOpenTrace}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Section
 * ------------------------------------------------------------------ */

/**
 * The `metrics` report section: every `ReportMetricRow`, grouped into
 * "Annual", "One-time", and "Rollout release" cadence groups. The section-level `.rv-h2` heading
 * itself is the composition's responsibility, not this component's.
 */
export function ReportMetricsList({
  metrics,
  ready,
  onOpenTrace,
}: ReportMetricsListProps) {
  const annual = metrics.filter((row) => row.cadence === "annual");
  const oneTime = metrics.filter((row) => row.cadence === "one-time");
  const rolloutRelease = metrics.filter(
    (row) => row.cadence === "one-time-with-rollout",
  );

  return (
    <div>
      {annual.length > 0 && (
        <>
          <ReportSectionHeading title="Annual" level="group" />
          <MetricRowsCard
            rows={annual}
            ready={ready}
            onOpenTrace={onOpenTrace}
          />
        </>
      )}

      {oneTime.length > 0 && (
        <>
          <ReportSectionHeading title="One-time" level="group" />
          <MetricRowsCard
            rows={oneTime}
            ready={ready}
            onOpenTrace={onOpenTrace}
          />
        </>
      )}
      {rolloutRelease.length > 0 && (
        <>
          <ReportSectionHeading title="Rollout release" level="group" />
          <MetricRowsCard
            rows={rolloutRelease}
            ready={ready}
            onOpenTrace={onOpenTrace}
          />
        </>
      )}
    </div>
  );
}
